import { ApolloClient, NormalizedCacheObject, useApolloClient, useQuery } from '@apollo/client'
import React from 'react'
import { createContainer } from 'unstated-next'

import { gql } from '../../__generated__/graphql/catalog/gql'
import { reportClientError } from '../../app/_components/chrome/scripts/DataDogRumScript'
import { QueryStatus } from '../../lib/hooks/types'

const FETCH_REVIEW_PRODUCT = gql(`
  query FetchReviewProduct($handle: String!, $colorKey: String) {
    productCatalogStandardProduct(handle: $handle) {
      __typename
      id
      handle
      variantGroup(color: $colorKey) {
        __typename
        id
        title
        seoTitle
        color {
          id
          displayValue
        }
        images(first: 1) {
          url
        }
        defaultVariant {
          shopifyProductId
        }
      }
    }
  }
`)

export type UseReviewsProductGroupDataOutput = {
  data: {
    colorId: string | undefined
    colorDisplayValue: string | undefined
    imageUrl: string | undefined
    seoTitle: string
    shopifyProductId: string
    title: string
    productGroupHandle: string
  } | null
  error: Error | undefined
  status: QueryStatus
}

type UseReviewsProductGroupDataProps = {
  handle: string
  colorKey: string | undefined | null
}

type PrimeClientForReviewsProductGroupDataProps = UseReviewsProductGroupDataProps & {
  client: ApolloClient<NormalizedCacheObject>
}

export const primeClientForReviewsProductGroupData = ({
  client,
  handle,
  colorKey,
}: PrimeClientForReviewsProductGroupDataProps) => {
  return client.query({
    query: FETCH_REVIEW_PRODUCT,
    variables: { handle, colorKey },
  })
}

export const useReviewsProductGroupData = ({
  handle,
  colorKey,
}: UseReviewsProductGroupDataProps): UseReviewsProductGroupDataOutput => {
  const apolloClient = useApolloClient()
  const ssrData = apolloClient.readQuery({
    query: FETCH_REVIEW_PRODUCT,
    variables: { handle, colorKey },
  })

  const { data, called, loading, error, previousData } = useQuery(FETCH_REVIEW_PRODUCT, {
    variables: { handle, colorKey },
    onError(error) {
      reportClientError({
        error,
        context: {
          scope: 'Reviews Page',
          label: 'query failed',
        },
      })
    },
  })

  const product = React.useMemo(() => {
    return (
      data?.productCatalogStandardProduct ??
      previousData?.productCatalogStandardProduct ??
      ssrData?.productCatalogStandardProduct ??
      null
    )
  }, [
    data?.productCatalogStandardProduct,
    previousData?.productCatalogStandardProduct,
    ssrData?.productCatalogStandardProduct,
  ])

  const status = React.useMemo<QueryStatus>(() => {
    if (loading) return 'pending'
    if (error) return 'rejected'
    if (!called && !product) return 'idle'
    return 'resolved'
  }, [called, error, loading, product])

  return React.useMemo(() => {
    const data = product
      ? {
          colorId: product.variantGroup.color?.id,
          colorDisplayValue: product.variantGroup.color?.displayValue,
          imageUrl: product.variantGroup.images?.[0]?.url,
          seoTitle: product.variantGroup.seoTitle,
          shopifyProductId: product.variantGroup.defaultVariant.shopifyProductId,
          title: product.variantGroup.title,
          productGroupHandle: product.handle,
        }
      : null

    return {
      data,
      status,
      error,
    }
  }, [product, status, error])
}

const useReviewsProductGroupDataWrapper = (
  initialState: UseReviewsProductGroupDataProps | undefined
) => {
  return useReviewsProductGroupData(initialState!)
}
const ReviewsProductGroupDataContainer = createContainer(useReviewsProductGroupDataWrapper)
export const ReviewsProductGroupDataProvider = ({
  children,
  ...initialState
}: UseReviewsProductGroupDataProps & {
  children: React.ReactNode
}) => {
  return (
    <ReviewsProductGroupDataContainer.Provider initialState={initialState}>
      {children}
    </ReviewsProductGroupDataContainer.Provider>
  )
}
export const useReviewsProductGroupDataContext = () => {
  return ReviewsProductGroupDataContainer.useContainer()!
}
